<template>
  <div
    ref="menuRef"
    class="h-screen bg-gray-800/80 text-white overflow-hidden transition-all duration-300"
    :class="{ 'w-[150px]': !collapsed, 'w-0': collapsed }"
  >
    <div class="h-full p-4 w-[170px]">
      <!-- Header -->
      <div class="mb-8 text-xl font-bold overflow-hidden whitespace-nowrap">
        HeroGrow
      </div>

      <!-- Navigation Links -->
      <nav class="space-y-2">
        <div v-for="item in menu" :key="item.title" class="cursor-pointer">
          <!-- Regular menu items -->
          <div
            v-if="!item.child"
            @click="onItemClick(item)"
            class="p-2 rounded hover:bg-gray-700 transition-colors"
            :class="{ 'bg-gray-700': userStore.activeTab === item.href }"
            :data-track="
              JSON.stringify({
                id: `menu-${item.title.toLowerCase()}`,
                type: 'main-menu',
                href: item.href,
              })
            "
          >
            <span class="whitespace-nowrap">{{ item.title }}</span>
          </div>

          <!-- Dropdown items -->
          <div v-else class="space-y-1">
            <div
              @click="toggleDropdown(item.title)"
              class="p-2 rounded hover:bg-gray-700 transition-colors flex justify-between items-center"
              :data-track="
                JSON.stringify({
                  id: `menu-${item.title.toLowerCase()}`,
                  type: 'dropdown-header',
                })
              "
            >
              <span class="whitespace-nowrap">{{ item.title }}</span>
              <span
                class="transform transition-transform"
                :class="{ 'rotate-180': openDropdowns[item.title] }"
                >▼</span
              >
            </div>
            <transition
              name="slide"
              @enter="startTransition"
              @leave="endTransition"
            >
              <div
                v-if="openDropdowns[item.title]"
                class="ml-4 space-y-1 overflow-hidden"
              >
                <div
                  v-for="child in item.child"
                  :key="child.title"
                  @click="onItemClick(child)"
                  class="p-2 rounded hover:bg-gray-700 transition-colors"
                  :class="{ 'bg-gray-700': userStore.activeTab === child.href }"
                  :data-track="
                    JSON.stringify({
                      id: `submenu-${item.title.toLowerCase()}-${child.title.toLowerCase()}`,
                      type: 'submenu-item',
                      parent: item.title,
                      href: child.href,
                    })
                  "
                >
                  {{ child.title }}
                </div>
              </div>
            </transition>
          </div>
        </div>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, computed } from "vue";
import { useUser } from "@/stores";
import { useWindowWidth } from "@/composables/useWindowWidth";

const userStore = useUser();
const menuRef = ref(null);
const openDropdowns = ref({
  Help: false, // Initialize Help dropdown state
});

const { width } = useWindowWidth();
const isMobile = computed(() => width.value <= 850);

// Handle clicks outside the menu
const handleClickOutside = (event) => {
  const isToggleButton = event.target.closest("[data-menu-toggle]");
  if (
    isMobile.value &&
    menuRef.value &&
    !menuRef.value.contains(event.target) &&
    !props.collapsed &&
    !isToggleButton
  ) {
    // Emit event to parent to collapse menu
    emit("update:collapsed", true);
  }
};

// Setup and cleanup click handler
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

// Watch for activeTab changes to auto-expand Help section when needed
watch(
  () => userStore.activeTab,
  (newTab) => {
    // Check if the new tab is one of the Help section's child routes
    const helpRoutes =
      menu
        .find((item) => item.title === "Help")
        ?.child.map((child) => child.href) || [];
    if (helpRoutes.includes(newTab)) {
      openDropdowns.value.Help = true;
    }
  }
);

const emit = defineEmits(["update:collapsed"]);
const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
});

const menu = [
  {
    title: "Dashboard",
    href: "/explorer/dashboard",
  },
  {
    title: "Reviews",
    href: "/explorer/reviews",
  },
  {
    title: "Reviewers",
    href: "/explorer/reviewers",
  },
  {
    title: "Settings",
    href: "/explorer/settings",
  },
  {
    title: "Help",
    child: [
      {
        title: "ChangeLog",
        href: "/explorer/changelog",
      },
      {
        title: "FAQ",
        href: "/explorer/faq",
      },
      {
        title: "Contact",
        href: "/explorer/contact",
      },
    ],
  },
  {
    title: "Logout",
    href: "/logout",
  },
];

import { useRouter } from "vue-router";

const router = useRouter();

const onItemClick = async (item) => {
  if (item.href === "/logout") {
    try {
      await userStore.signOutUser();
      router.push("/login");
    } catch (error) {
      console.error("Error during logout:", error);
      // Still try to navigate to login if logout fails
      router.push("/login");
    }
  } else {
    userStore.activeTab = item.href;
  }
};

const toggleDropdown = (title) => {
  openDropdowns.value[title] = !openDropdowns.value[title];
};

const startTransition = (el) => {
  el.style.maxHeight = el.scrollHeight + "px";
};

const endTransition = (el) => {
  el.style.maxHeight = "0px";
};
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: max-height 0.3s ease-in-out;
  max-height: 200px; /* Adjust based on content */
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
}
</style>
